// ===========================================
// Meet & Greet Availability Settings Component
// ===========================================
// Configure days and time slots for meet & greet sessions

import { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import {
  useMeetAndGreetSettings,
  useUpdateMeetAndGreetSettings,
  useAddMeetAndGreetTimeSlot,
  useDeleteMeetAndGreetTimeSlot,
} from '../../hooks/useMeetAndGreet';
import { usePermissions } from '../../hooks/usePermissions';
import type { MeetAndGreetTimeSlot } from '../../api/meetAndGreet.api';

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

const formatTime = (time: string) => {
  const [hours, minutes] = time.split(':');
  const h = parseInt(hours, 10);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${minutes} ${ampm}`;
};

export default function MeetAndGreetAvailabilitySettings() {
  const { data: settings, isLoading } = useMeetAndGreetSettings();
  const updateSettingsMutation = useUpdateMeetAndGreetSettings();
  const addSlotMutation = useAddMeetAndGreetTimeSlot();
  const deleteSlotMutation = useDeleteMeetAndGreetTimeSlot();
  // Mirror the routes: PATCH /settings and POST /settings/time-slots need write, DELETE needs delete.
  const { can } = usePermissions();
  const canWrite = can('meet_and_greet', 'write');
  const canDelete = can('meet_and_greet', 'delete');

  // Local state for editing settings
  const [sessionDuration, setSessionDuration] = useState<number | null>(null);
  const [bufferTime, setBufferTime] = useState<number | null>(null);
  const [advanceBookingDays, setAdvanceBookingDays] = useState<number | null>(null);
  const [minimumNoticeHours, setMinimumNoticeHours] = useState<number | null>(null);

  // Dialog state for adding time slot
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newSlot, setNewSlot] = useState({
    dayOfWeek: 1, // Monday
    startTime: '09:00',
    endTime: '10:00',
  });

  // Initialize local state when settings load
  if (settings && sessionDuration === null) {
    setSessionDuration(settings.sessionDuration);
    setBufferTime(settings.bufferTime);
    setAdvanceBookingDays(settings.advanceBookingDays);
    setMinimumNoticeHours(settings.minimumNoticeHours);
  }

  const handleSaveSettings = () => {
    updateSettingsMutation.mutate({
      sessionDuration: sessionDuration ?? undefined,
      bufferTime: bufferTime ?? undefined,
      advanceBookingDays: advanceBookingDays ?? undefined,
      minimumNoticeHours: minimumNoticeHours ?? undefined,
    });
  };

  const handleAddSlot = () => {
    addSlotMutation.mutate(newSlot, {
      onSuccess: () => {
        setAddDialogOpen(false);
        setNewSlot({ dayOfWeek: 1, startTime: '09:00', endTime: '10:00' });
      },
    });
  };

  const handleDeleteSlot = (slotId: string) => {
    if (confirm('Are you sure you want to delete this time slot?')) {
      deleteSlotMutation.mutate(slotId);
    }
  };

  // Group time slots by day
  const slotsByDay = (settings?.timeSlots ?? []).reduce((acc, slot) => {
    const day = slot.dayOfWeek;
    if (!acc[day]) acc[day] = [];
    acc[day].push(slot);
    return acc;
  }, {} as Record<number, MeetAndGreetTimeSlot[]>);

  if (isLoading) {
    return (
      <Paper sx={{ p: 3 }}>
        <Skeleton variant="text" width={200} height={32} />
        <Skeleton variant="text" width="100%" height={24} sx={{ mt: 1 }} />
        <Grid container spacing={2} sx={{ mt: 2 }}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={6} sm={3} key={i}>
              <Skeleton variant="rectangular" height={56} />
            </Grid>
          ))}
        </Grid>
      </Paper>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* General Settings */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          General Settings
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Configure the duration and booking rules for meet & greet sessions.
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Session Duration"
              type="number"
              fullWidth
              disabled={!canWrite}
              value={sessionDuration ?? ''}
              onChange={(e) => setSessionDuration(parseInt(e.target.value) || 0)}
              InputProps={{ endAdornment: <Typography color="text.secondary">min</Typography> }}
              helperText="Length of each meet & greet"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Buffer Time"
              type="number"
              fullWidth
              disabled={!canWrite}
              value={bufferTime ?? ''}
              onChange={(e) => setBufferTime(parseInt(e.target.value) || 0)}
              InputProps={{ endAdornment: <Typography color="text.secondary">min</Typography> }}
              helperText="Time between sessions"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Advance Booking"
              type="number"
              fullWidth
              disabled={!canWrite}
              value={advanceBookingDays ?? ''}
              onChange={(e) => setAdvanceBookingDays(parseInt(e.target.value) || 0)}
              InputProps={{ endAdornment: <Typography color="text.secondary">days</Typography> }}
              helperText="How far in advance to book"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Minimum Notice"
              type="number"
              fullWidth
              disabled={!canWrite}
              value={minimumNoticeHours ?? ''}
              onChange={(e) => setMinimumNoticeHours(parseInt(e.target.value) || 0)}
              InputProps={{ endAdornment: <Typography color="text.secondary">hrs</Typography> }}
              helperText="Required notice before booking"
            />
          </Grid>
        </Grid>

        {canWrite && (
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSaveSettings}
              disabled={updateSettingsMutation.isPending}
            >
              {updateSettingsMutation.isPending ? 'Saving...' : 'Save Settings'}
            </Button>
          </Box>
        )}
      </Paper>

      {/* Available Time Slots */}
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h6" gutterBottom>
              Available Time Slots
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Define the days and times when meet & greet sessions can be booked.
            </Typography>
          </Box>
          {canWrite && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setAddDialogOpen(true)}
            >
              Add Time Slot
            </Button>
          )}
        </Box>

        {(settings?.timeSlots ?? []).length === 0 ? (
          <Alert severity="warning" sx={{ mt: 2 }}>
            No time slots configured. Add time slots to enable meet & greet bookings.
          </Alert>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Day</TableCell>
                  <TableCell>Start Time</TableCell>
                  <TableCell>End Time</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {DAYS_OF_WEEK.map((day) => {
                  const daySlots = slotsByDay[day.value] || [];
                  if (daySlots.length === 0) return null;

                  return daySlots.map((slot, index) => (
                    <TableRow key={slot.id}>
                      {index === 0 && (
                        <TableCell rowSpan={daySlots.length} sx={{ fontWeight: 500 }}>
                          {day.label}
                        </TableCell>
                      )}
                      <TableCell>{formatTime(slot.startTime)}</TableCell>
                      <TableCell>{formatTime(slot.endTime)}</TableCell>
                      <TableCell>
                        <Chip
                          label={slot.isActive ? 'Active' : 'Inactive'}
                          size="small"
                          color={slot.isActive ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        {canDelete && (
                          <IconButton
                            size="small"
                            color="error"
                            aria-label="Delete time slot"
                            onClick={() => handleDeleteSlot(slot.id)}
                            disabled={deleteSlotMutation.isPending}
                          >
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ));
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Summary by day */}
        <Divider sx={{ my: 3 }} />
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Quick Overview
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {DAYS_OF_WEEK.map((day) => {
            const count = slotsByDay[day.value]?.length || 0;
            return (
              <Chip
                key={day.value}
                label={`${day.label.substring(0, 3)}: ${count}`}
                size="small"
                color={count > 0 ? 'primary' : 'default'}
                variant={count > 0 ? 'filled' : 'outlined'}
              />
            );
          })}
        </Box>
      </Paper>

      {/* Add Time Slot Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Time Slot</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Day of Week</InputLabel>
                <Select
                  value={newSlot.dayOfWeek}
                  label="Day of Week"
                  onChange={(e) => setNewSlot({ ...newSlot, dayOfWeek: e.target.value as number })}
                >
                  {DAYS_OF_WEEK.map((day) => (
                    <MenuItem key={day.value} value={day.value}>
                      {day.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Start Time"
                type="time"
                fullWidth
                value={newSlot.startTime}
                onChange={(e) => setNewSlot({ ...newSlot, startTime: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="End Time"
                type="time"
                fullWidth
                value={newSlot.endTime}
                onChange={(e) => setNewSlot({ ...newSlot, endTime: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddSlot}
            disabled={addSlotMutation.isPending}
          >
            {addSlotMutation.isPending ? 'Adding...' : 'Add Slot'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
